import { DeadZone, KalmanFilter1D, OneEuroFilter } from '../utils/filters';
import { BALANCE } from '../config/balance';

export interface HeadPose {
  /** Viewer position in metres, relative to the calibrated neutral pose. */
  x: number;
  y: number;
  z: number;
}

/** Which smoother the pose pipeline runs. `none` is the `?nosmooth` bisect. */
export type HeadPoseFilterMode = 'kalman' | 'oneEuro' | 'none';

/** The two smoothing back-ends share this shape, so the mode picks a table. */
interface ScalarFilter {
  filter(value: number, dt: number): number;
  reset(seed?: number): void;
}

type Axes = { x: ScalarFilter; y: ScalarFilter; z: ScalarFilter };

function zeroPose(): HeadPose {
  return { x: 0, y: 0, z: 0 };
}

/**
 * Turns raw head-position samples into a stable viewer offset.
 *
 * The off-axis window only needs translation, so the live pipeline deliberately
 * ignores rotation. The filter is:
 *
 *   raw position -> subtract calibrated neutral -> dead zone -> clamp -> smoothing
 *
 * The smoother is a per-axis **constant-velocity Kalman** filter by default: it
 * carries velocity, so a real lean tracks quickly while jitter and glitch
 * samples are rejected, and it runs on the whole signal at all times (the old
 * design only smoothed during bullet time, which left normal play raw and put a
 * second lag stage on the dodge). `?nosmooth` swaps in a pass-through and
 * `?leanlegacy` restores the One Euro filter.
 */
export class HeadPoseFilter {
  private readonly mode: HeadPoseFilterMode;
  private neutral: HeadPose | null = null;
  private readonly kalman: Axes = {
    x: new KalmanFilter1D(
      BALANCE.headTracking.kalmanProcessNoise,
      BALANCE.headTracking.kalmanMeasurementNoise,
      BALANCE.headTracking.kalmanGate,
    ),
    y: new KalmanFilter1D(
      BALANCE.headTracking.kalmanProcessNoise,
      BALANCE.headTracking.kalmanMeasurementNoise,
      BALANCE.headTracking.kalmanGate,
    ),
    z: new KalmanFilter1D(
      BALANCE.headTracking.kalmanProcessNoise,
      BALANCE.headTracking.kalmanMeasurementNoise,
      BALANCE.headTracking.kalmanGate,
    ),
  };
  private readonly euro: Axes = {
    x: new OneEuroFilter({
      minCutoff: BALANCE.headTracking.minCutoff,
      beta: BALANCE.headTracking.beta,
    }),
    y: new OneEuroFilter({
      minCutoff: BALANCE.headTracking.minCutoff,
      beta: BALANCE.headTracking.beta,
    }),
    z: new OneEuroFilter({
      minCutoff: BALANCE.headTracking.minCutoff,
      beta: BALANCE.headTracking.beta,
    }),
  };
  private readonly viewDeadZone = new DeadZone(BALANCE.headTracking.viewDeadZone);

  constructor(options: { mode?: HeadPoseFilterMode } = {}) {
    this.mode = options.mode ?? 'kalman';
  }

  /** Remember the current pose as the neutral (zero) point. */
  calibrate(pose: HeadPose): void {
    this.neutral = { ...pose };
  }

  get isCalibrated(): boolean {
    return this.neutral !== null;
  }

  /** Process a raw position and return the filtered offset, in metres. */
  update(pose: HeadPose, dt: number): HeadPose {
    if (!this.neutral) {
      this.calibrate(pose);
      return zeroPose();
    }

    const neutral = this.neutral;
    const x = this.smooth('x', this.viewDeadZone.apply(pose.x - neutral.x), dt);
    const y = this.smooth('y', this.viewDeadZone.apply(pose.y - neutral.y), dt);
    const z = this.smooth('z', this.viewDeadZone.apply(pose.z - neutral.z), dt);

    return {
      x: clampValue(x, BALANCE.headTracking.viewLimitX),
      y: clampValue(y, BALANCE.headTracking.viewLimitY),
      z: clampValue(z, BALANCE.headTracking.viewLimitZ),
    };
  }

  private smooth(axis: keyof Axes, value: number, dt: number): number {
    switch (this.mode) {
      case 'none':
        return value;
      case 'oneEuro':
        return this.euro[axis].filter(value, dt);
      default:
        return this.kalman[axis].filter(value, dt);
    }
  }

  reset(): void {
    this.neutral = null;
    for (const filter of [...Object.values(this.kalman), ...Object.values(this.euro)]) {
      filter.reset();
    }
  }

  /** Metres of the current offset, for the debug overlay. */
  describe(pose: HeadPose): HeadPose {
    if (!this.neutral) return zeroPose();
    return {
      x: pose.x - this.neutral.x,
      y: pose.y - this.neutral.y,
      z: pose.z - this.neutral.z,
    };
  }
}

function clampValue(value: number, limit: number): number {
  return Math.max(-limit, Math.min(limit, value));
}
